import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { trigger, style, animate, transition } from '@angular/animations';
import { Observable, Subscription } from 'rxjs';

import { AuthService } from '@services/http/auth.service';
import { Notification } from '@models/notifications/notification.model';
import { NotificationService } from '@services/http/notification.service';
import { SidebarService } from '@components/sidebar/sidebarService.service';
import { StateService } from '@services/Logic/state.service';
import { StorageService } from '@services/Logic/storage.service';
import { ToastrService } from 'ngx-toastr';

import { MatIconModule } from '@angular/material/icon';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { SharedPipeModule } from '@app/shared/pipes/shared.pipe.module';

import { SlugifyPipe } from '@app/pipes/slugify.pipe';

import { CancellationGuardrailComponent } from '../cancellationGuardrail/cancellationGuardrail.component';
import { SvgStarComponent } from '@components/seoPageElements/svg-star/svg-star.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    SvgStarComponent,
    SharedPipeModule,
    InfiniteScrollModule,
    MatTabsModule,
    RouterLink,
    CancellationGuardrailComponent,
  ],
  animations: [
    trigger('sidebarAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('500ms', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)' }),
        animate('500ms', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private addNewNotificationSidebarCompnentRef: Subscription =
    new Subscription();
  private showSidebarComponentRef: Subscription = new Subscription();
  private initNewNotificationsHeaderComponentRef: Subscription =
    new Subscription();
  private showNewMessagesHeaderComponentRef: Subscription = new Subscription();
  private fetchNewNotificationsCompRef: Subscription = new Subscription();
  private initLanguageRef: Subscription = new Subscription();

  public showHandyhandTab: boolean = false;
  public isContainerVisible: boolean;
  public otherNotifications: Notification[] = [];
  public newTaskNotifications: Notification[] = [];
  public greetingString: string;
  public cancellationGuardrail = true;
  public settingsTabs: string[];
  public sideBarTabs: any[];
  public sidebarTabsHandyhands: any[];
  public step = 0;
  public tabIndex = 0;
  public newMessagesCount: number;
  public visibleTab: number;

  private taskNotificationPageNumber = 1;
  private otherNotificationPageNumber = 1;
  public pageNumber = 1;
  private pageSize = 20;
  private isNotificationsLoaded = false;

  constructor(
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private sidebarService: SidebarService,
    private slugifyPipe: SlugifyPipe,
    public storage: StorageService,
    private stateService: StateService,
    private toastr: ToastrService,
  ) {
    this.visibleTab = -1;
    this.isContainerVisible = false;
    this.newMessagesCount = 0;
    this.storage.settingsTab = this.storage.settingsTab
      ? this.storage.settingsTab
      : 0;

    // Set default tab to Handyhand for Pinployee or both
    if (
      this.storage.userSettings?.pinployeeOrTaskOwner === 'pp' ||
      this.storage.userSettings?.pinployeeOrTaskOwner === 'both'
    ) {
      this.showHandyhandTab = true;
    }
  }

  public get user() {
    return this.storage.user;
  }

  public get userSettings() {
    return this.storage.userSettings;
  }

  public get lang() {
    return this.storage.lang;
  }

  ngOnInit() {
    this.subscribeShowSidebar();
    this.displayCancellationGuardrail();
    this.getGreetingBasedOnTime();
    this.fetchInitialNotifications();
    this.listenToSocketNewNotification();
    this.initializeSideBarTabs();
    this.getTranslations();
  }

  ngOnDestroy() {
    if (this.showSidebarComponentRef) {
      this.showSidebarComponentRef.unsubscribe();
    }
    if (this.initNewNotificationsHeaderComponentRef) {
      this.initNewNotificationsHeaderComponentRef.unsubscribe();
    }
    if (this.showNewMessagesHeaderComponentRef) {
      this.showNewMessagesHeaderComponentRef.unsubscribe();
    }
    if (this.addNewNotificationSidebarCompnentRef) {
      this.addNewNotificationSidebarCompnentRef.unsubscribe();
    }
    if (this.fetchNewNotificationsCompRef) {
      this.fetchNewNotificationsCompRef.unsubscribe();
    }
    if (this.initLanguageRef) this.initLanguageRef.unsubscribe();
    this.storage.settingsTab = 0;
  }

  getUnreadNotificationCount(): number {
    const count = this.otherNotifications.filter(
      (notification) => !notification.isRead,
    ).length;
    return Math.min(count, 20);
  }

  getUnreadTaskNotificationCount(): number {
    const count = this.newTaskNotifications.filter(
      (notification) => !notification.isRead,
    ).length;
    return Math.min(count, 20);
  }

  private subscribeShowSidebar() {
    this.showSidebarComponentRef =
      this.stateService.showSidebarComponentSource.subscribe(
        (tabIndex: number) => {
          this.visibleTab = tabIndex;
          this.isContainerVisible = true;
          if (this.otherNotifications.length === 0) {
            this.fetchInitialNotifications();
          }
        },
      );
  }

  private fetchInitialNotifications() {
    this.notificationService
      .getNewNotificationsPaginated(20, 1)
      .subscribe((res: Notification) => {
        this.otherNotifications = res.notifications;
      });
    this.notificationService
      .getNewTaskNotificationsPaginated(30, 1)
      .subscribe((res: Notification) => {
        this.newTaskNotifications = res.notifications;
      });
  }

  private initializeSideBarTabs(): void {
    this.sideBarTabs = this.sidebarService.getSideBarTabs();
    this.sidebarTabsHandyhands = this.sidebarService.getSidebarTabsHandyhands(
      this.user,
    );
  }

  private listenToSocketNewNotification() {
    this.addNewNotificationSidebarCompnentRef =
      this.stateService.addNewNotificationToSidebarComponentSource.subscribe(
        (newNotif: Notification) => {
          if (newNotif.type === 'new_task') {
            this.newTaskNotifications = [
              newNotif,
              ...this.newTaskNotifications,
            ];
            this.newTaskNotifications.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            );
          } else {
            this.otherNotifications = [newNotif, ...this.otherNotifications];
            this.otherNotifications.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            );
          }
          this.showNewMessagesHeaderComponentRef =
            this.stateService.showNewMessagesHeaderComponentSource.subscribe(
              (newCount: number) => {
                this.newMessagesCount = newCount;
              },
            );
        },
      );
  }

  private displayCancellationGuardrail() {
    const { tasksCompleted, cancellationRate, completionRate } =
      this.storage.user;
    const isTaskOwner = this.user.pinployeeOrTaskOwner === 'to';
    this.cancellationGuardrail = this.sidebarService.isAccountAtRisk(
      tasksCompleted,
      completionRate,
      cancellationRate,
      isTaskOwner,
    );
  }

  private getTranslations() {
    this.settingsTabs = this.sidebarService.getSettingsTab();
  }

  public handleSideBarClick(tab) {
    if (tab.url !== '') {
      this.closeSidebar(tab.url);
    } else {
      tab.expanded = !tab.expanded;
    }
  }

  public closeSidebar(
    path: string = null,
    params: any = null,
    fragment: string = null,
  ) {
    this.visibleTab = -1;
    this.stateService.closeSidebar();
    setTimeout(() => {
      this.isContainerVisible = false;
      if (path === '/') {
        this.authService.logout();
        this.stateService.hideHeaderFooterComponentSource.next(false);
      }
    }, 300);

    this.isContainerVisible = false;
    if (path) {
      const currentRoute = this.router.url.split('?')[0];
      if (path === '/login') {
        this.authService.logout();
      } else if (params) {
        if (currentRoute === `${path}/${params}`) {
          this.stateService.fetchTaskDetails();
        } else {
          this.router.navigate([`${path}${params}`], { fragment });
        }
      } else {
        this.router.navigateByUrl(path);
      }
    }
  }

  public getGreetingBasedOnTime(): void {
    this.greetingString = this.sidebarService.getGreetingMsg();
  }

  public toggleHandyhandSettingsTab(input: boolean) {
    this.showHandyhandTab = input;
  }

  public keepSidebarOpened(e) {
    e.stopPropagation();
  }

  public markNotifReadAll() {
    this.notificationService
      .markAllNotificationsAsRead()
      .subscribe((result) => {
        if (result.notificationsUpdated > 0) {
          this.toastr.success(
            `${result.notificationsUpdated} Notifikationer opdateret`,
          );
          this.otherNotifications = this.otherNotifications.map(
            (n: Notification) => {
              n.isRead = true;
              return n;
            },
          );
          this.newTaskNotifications = this.newTaskNotifications.map(
            (n: Notification) => {
              n.isRead = true;
              return n;
            },
          );
        }
        this.stateService.initNewNotificationsHeader(0);
      });
  }

  public constructUrlForItemType(item: any) {
    return this.sidebarService.constructUrlForItem(item);
  }

  public getAdditionalParam(item) {
    return this.sidebarService.additionalParam(item);
  }

  public openPage(item: Notification): void {
    const notificationsArray: Notification[] =
      item.type === 'new_task' || item.type === 'not_enough_offers_in_48h'
        ? this.newTaskNotifications
        : this.otherNotifications;
    const notificationIndex: number = notificationsArray.findIndex(
      (n: Notification) => n.id === item.id,
    );
    if (notificationIndex !== -1) {
      this.notificationService.readNotification(item.id).subscribe(() => {
        this.stateService.readNotification();
        notificationsArray[notificationIndex].isRead = true;

        const url = this.constructUrlForItemType(item) as any;
        const directURls: string[] = [
          'book_again',
          'complete_profile',
          'create_first_task',
          'to-reactivation-1',
          'to-reactivation-2',
          'to-reactivation-3',
          'to-reactivation-4',
          'to-reactivation-5',
          'conversation-created',
          'offer_request_created',
          'offer_request_offer_made',
          'offer_request_accepted',
          'company_status_update',
          'date-accepted',
          'support_ticket_answer',
          'create_support_ticket',
          'create_support_ticket_from_admin',
        ];
        if (directURls.includes(item.type)) {
          return this.closeSidebar(url);
        }
        if (url) {
          this.closeSidebar(
            url,
            this.slugifyURL(item),
            this.getAdditionalParam(item),
          );
        }
      });
    }
  }

  public slugifyURL(item): string {
    return `${this.slugifyPipe.transform(
      `${item?.task?.title} ${item?.task?.city}`,
    )}/${item.taskId}`;
  }

  public loadNextNotification() {
    if (this.isNotificationsLoaded) {
      return;
    }
    if (this.tabIndex === 0) {
      this.otherNotificationPageNumber += 1;
      this.fetchNotifications('other', this.otherNotificationPageNumber);
    } else if (this.tabIndex === 1) {
      this.taskNotificationPageNumber += 1;
      this.fetchNotifications('task', this.taskNotificationPageNumber);
    }
  }

  private fetchNotifications(type: 'task' | 'other', pageNumber: number) {
    this.stateService.toggleWaitingPage(true);
    const serviceCall: Observable<Notification> =
      type === 'task'
        ? this.notificationService.getNewTaskNotificationsPaginated(
            this.pageSize,
            pageNumber,
          )
        : this.notificationService.getNewNotificationsPaginated(
            this.pageSize,
            pageNumber,
          );

    serviceCall.subscribe({
      next: (res: Notification) => {
        const notifications: Notification[] =
          type === 'task' ? this.newTaskNotifications : this.otherNotifications;
        res.notifications.forEach((n) =>
          notifications.push(new Notification(n)),
        );
        if (!res.hasMore) {
          this.isNotificationsLoaded = true;
        }
        this.stateService.toggleWaitingPage(false);
      },
      error: () => {
        this.stateService.toggleWaitingPage(false);
      },
    });
  }

  public onTabChange(event: MatTabChangeEvent) {
    this.tabIndex = event.index;
  }
}
